import { Box, Button, Checkbox, Divider, Grid2, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { ClosingChecklistListTypeEnum } from "../../../_common/enum/closingChecklistListType.enum";
import { StandardList } from "../../../_components/list/standardList.component";
import { StandardListItem } from "../../../_components/list/standardListItem.component";
import { getDateFromString } from "../../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { ticketClosingChecklistService } from "../../../_services/ticketClosingChecklist.service";
import { LIGHT_SUCCESS_COLOR, LIGHT_WARNING_COLOR } from "../../../_const/color.const";
import { TicketClosingChecklistAgentDrawer } from "./ticket-closingChecklistAgentDrawer";
import { toastr } from "../../../_components/toastr/toastr";
import { accountService } from "../../../_services/accounts.service";


export function TicketClosingChecklistListAgent(params: { closingChecklistListTypeId: ClosingChecklistListTypeEnum, paymentDate?: string, invoiceNumber?: string }) {

    const { id } = useParams<{ id: string }>();
    const [data, setData] = useState<{ [key: number]: any }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [actions, setActions] = useState<any[]>([]);
    const navigate = useNavigate();
    const isPurchaseOrderGenerated = actions.some((group: any) => !!group?.purchaseOrderNumber);

    useEffect(() => {
        if (id != null) {
            fetchData();
        }
        else setData({});
    }, [id]);

    function fetchData() {
        if (!id) return;
        setIsLoading(true);
        return ticketClosingChecklistService
            .getAll(id, params.closingChecklistListTypeId)
            .then(
                (json) => {
                    setActions(json);
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function setPrimaryAgent(ticketAgentId: number) {
        if (!id) return;
        return ticketClosingChecklistService
            .setPrimaryAgent(id, ticketAgentId)
            .then(
                () => {
                    toastr.success("Primary agent updated");
                    fetchData();
                },
                handleErrorResponse
            );
    }

    function disable(id: string) {
        if (!id) return;
        setIsLoading(true);
        return ticketClosingChecklistService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }


    return (
        <>
            {(!isLoading && (!actions || !actions.length)) &&
                <Grid2 size={{ sm: 12 }}>
                    <Box display="flex" alignItems="center" justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
                        <Typography color="text.secondary" fontStyle="italic">
                            No associated items found
                        </Typography>
                    </Box>
                </Grid2>
            }
            {(!!actions && !!actions.length) && actions.map((group, gIndex) => {
                const actionsTotal = group.items.reduce(
                    (sum: number, action: any) =>
                        sum + (Number(action?.price ?? 0) * Number(action?.quantity ?? 1) || 0),
                    0
                );

                const ticketAgentId = group?.items?.find((_item: any) => _item?.ticketAgentId != null)?.ticketAgentId ?? null;
                return (
                    <Grid2 key={gIndex} >
                        {/* Agent info */}
                        {(accountService.isAdmin &&
                            <Grid2 size={{ xs: 12 }} mt={1}>
                                <Box px={1} py={1} display="flex" alignItems="center" justifyContent="space-between"
                                    sx={{
                                        backgroundColor: group.isPrimaryAgent ? LIGHT_SUCCESS_COLOR : LIGHT_WARNING_COLOR,
                                    }}
                                >
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Agent:</strong> {group.agentName}
                                    </Typography>

                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body1" mr={1}>
                                            <strong>Primary: </strong>
                                        </Typography>

                                        <Checkbox
                                            checked={group.isPrimaryAgent}
                                            onChange={() => setPrimaryAgent(ticketAgentId)}
                                            color="primary"
                                        />

                                    </Box>
                                </Box>
                            </Grid2>
                        )}

                        {(accountService.isAdmin && !isPurchaseOrderGenerated) &&
                            <Box display="flex" justifyContent="flex-end" my={2}>
                                <Button variant="contained" onClick={() => navigate(`ManageClosingChecklistAgent/Create/${ticketAgentId}`)}>
                                    Add List Item
                                </Button>
                            </Box>
                        }

                        <StandardList isLoading={isLoading}>
                            {group.items.map((item: any, index: any) => (
                                <StandardListItem
                                    primary={item.name}
                                    secondary={item?.quantity ? "Qty: " + (item?.quantity) : null}
                                    tertiary={item?.price ? (item?.price).toFixed(2) : null}
                                    buttons={[
                                        { icon: "edit", onClick: () => { navigate(`ManageClosingChecklistAgent/Update/${item.id}`) }, variant: "primary", disabled: isPurchaseOrderGenerated },
                                        { icon: "delete", onClick: () => { disable(item.id) }, variant: "error", disabled: isPurchaseOrderGenerated },
                                    ]
                                    }
                                    key={item.id}

                                />
                            ))}

                        </StandardList>
                        {/* Totals */}
                        <Grid2 size={{ xs: 12 }} mt={2}>
                            <Box px={1}>
                                <Typography variant="body1" gutterBottom>
                                    <strong>
                                        Total:
                                    </strong> {'\u20AC'}{actionsTotal.toFixed(2)}
                                </Typography>

                                {params?.paymentDate &&
                                    <Typography variant="h6" gutterBottom>
                                        <strong>Payment Date:</strong> {params?.paymentDate != null ? getDateFromString(params.paymentDate) : ""}
                                    </Typography>
                                }
                            </Box>

                            <Divider sx={{ my: 2 }} />
                        </Grid2>
                    </Grid2>
                )
            })}

            <Routes>
                <Route path="ManageClosingChecklistAgent/Create/:ticketAgentId" element={<TicketClosingChecklistAgentDrawer closingChecklistListTypeId={ClosingChecklistListTypeEnum.Agent} onSave={() => { fetchData(); navigate(""); }} onClose={() => { navigate(""); }} />} />
                <Route path="ManageClosingChecklistAgent/Update/:checklistItemId" element={<TicketClosingChecklistAgentDrawer closingChecklistListTypeId={ClosingChecklistListTypeEnum.Agent} onSave={() => { fetchData(); navigate(""); }} onClose={() => { navigate(""); }} />} />


            </Routes>
        </>
    );

}
